// Full-size originals of the photos of a send. chat-api only accepts an
// original once the chat row and the resized copy exist, which POST /chat
// writes before its reply starts streaming: the originals of a send are held
// until then, and dropped if the send fails first. Released originals upload
// one at a time, so up to 15 MB each does not compete with the streaming
// reply for bandwidth.
//
// An attachment id stays pending while it is held or uploading, at most
// `waitMs`: add_property_images approvals wait on it so the tool reads the
// original rather than falling back to the resized copy.

export type OriginalUpload<F> = {
  chatId: string;
  attachmentId: string;
  file: F;
};

export const ORIGINAL_UPLOAD_WAIT_MS = 120_000;

export type OriginalUploadQueue<F> = {
  hold: (uploads: Array<OriginalUpload<F>>) => void;
  // Resolves once every upload released so far has finished.
  release: () => Promise<void>;
  drop: () => void;
  subscribe: (listener: () => void) => () => void;
  // Same Set instance until the pending ids change (useSyncExternalStore).
  getPendingIds: () => ReadonlySet<string>;
};

export const createOriginalUploadQueue = <F>({
  upload,
  waitMs = ORIGINAL_UPLOAD_WAIT_MS,
}: {
  // Must not reject: a failed upload only means the copy is used instead.
  upload: (params: OriginalUpload<F>) => Promise<void>;
  waitMs?: number;
}): OriginalUploadQueue<F> => {
  let held: Array<OriginalUpload<F>> = [];
  let chain: Promise<void> = Promise.resolve();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const listeners = new Set<() => void>();
  let pendingIds: ReadonlySet<string> = new Set();

  const publish = () => {
    pendingIds = new Set(timers.keys());
    for (const listener of listeners) {
      listener();
    }
  };

  const settle = (attachmentId: string) => {
    const timer = timers.get(attachmentId);
    if (timer === undefined) {
      return;
    }
    clearTimeout(timer);
    timers.delete(attachmentId);
    publish();
  };

  return {
    hold: uploads => {
      if (uploads.length === 0) {
        return;
      }
      held = [...held, ...uploads];
      for (const { attachmentId } of uploads) {
        const previous = timers.get(attachmentId);
        if (previous !== undefined) {
          clearTimeout(previous);
        }
        timers.set(
          attachmentId,
          setTimeout(() => settle(attachmentId), waitMs),
        );
      }
      publish();
    },
    release: () => {
      const batch = held;
      held = [];
      for (const item of batch) {
        chain = chain
          .then(() => upload(item))
          .catch(() => {})
          .then(() => settle(item.attachmentId));
      }
      return chain;
    },
    drop: () => {
      const batch = held;
      held = [];
      for (const { attachmentId } of batch) {
        settle(attachmentId);
      }
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getPendingIds: () => pendingIds,
  };
};
